/**
 * A friend's table invite (D-048, p34-friend-invite): a card with Join and Not now over any screen except a table.
 * At a table (D-064, p41-invite-at-table) it is a slim strip instead: no Join mid-round, it slides away after 8 s and
 * the invite waits on Home ("Invited to a table"); Not now declines. An invite already shown either way never pops again.
 */
import { useEffect, useReducer, useState, type ReactElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { declineInvite, joinInvite, useTableInvites } from "../lib/invites";
import { errorSentence as errorMessage } from "../lib/api";
import { Avatar } from "./Avatar";
import { Button } from "./Button";

/** Invites already shown as the at-table strip; they wait on Home instead of popping up again, across reloads in this tab. */
const KEPT_KEY = "digu.invites.kept";
const kept = {
  read(): string[] { try { return JSON.parse(sessionStorage.getItem(KEPT_KEY) ?? "[]") as string[]; } catch { return []; } },
  has(code: string): boolean { return this.read().includes(code); },
  add(code: string): void { try { sessionStorage.setItem(KEPT_KEY, JSON.stringify([...this.read().filter((c) => c !== code), code].slice(-20))); } catch { /* storage off: the card may show again */ } },
};
export const STRIP_MS = 8_000;

export function TableInviteCard(): ReactElement | null {
  const { status } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [invites, drop] = useTableInvites(status === "signed-in");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const atTable = pathname.startsWith("/table/");
  const invite = invites.find((i) => !kept.has(i.code));
  useEffect(() => {
    if (!atTable || !invite) return;
    const id = setTimeout(() => { kept.add(invite.code); rerender(); }, STRIP_MS);
    return () => clearTimeout(id);
  }, [atTable, invite]);
  // Never over the lobby the invite points at.
  if (!invite || pathname === `/lobby/${invite.code}`) return null;
  const where = invite.tableName ? invite.tableName : invite.kind === "club" ? "Club table" : "Private";
  const join = async () => {
    setBusy(true); setError(null);
    try { const to = await joinInvite(invite.code); drop(invite.code); navigate(to); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const notNow = () => { drop(invite.code); setError(null); void declineInvite(invite.code); };
  if (atTable) {
    return (
      <div className="tinv tinv--strip" role="status" aria-live="polite" data-testid="table-invite-strip">
        <div className="tinv__strip">
          <Avatar publicId={invite.from.publicId} photoVersion={invite.from.photoVersion} name={invite.from.name} size={36} frame={invite.from.frame} />
          <div className="tinv__text">
            <p className="small-b">{invite.from.name} invited you to a table</p>
            <p className="cap text-secondary">Saved on Home for after this round.</p>
          </div>
          <button type="button" className="link tinv__not-now" onClick={notNow}>Not now</button>
        </div>
      </div>
    );
  }
  return (
    <div className="tinv" role="dialog" aria-modal="false" aria-labelledby="tinv-title" data-testid="table-invite">
      <div className="tinv__card">
        <div className="tinv__head">
          <Avatar publicId={invite.from.publicId} photoVersion={invite.from.photoVersion} name={invite.from.name} size={44} frame={invite.from.frame} />
          <div className="tinv__text">
            <p id="tinv-title" className="body-b">{invite.from.name} invited you to a table</p>
            <p className="small text-secondary">{where}, {invite.taken} of {invite.seats} seats taken. {invite.rules}</p>
          </div>
        </div>
        {error && <p className="small tinv__error" role="alert">{error}</p>}
        <div className="tinv__actions">
          {error ? <Button kind="quiet" size={48} onClick={notNow}>Close</Button> : <>
            <Button size={48} onClick={() => void join()} busy={busy}>Join</Button>
            <Button kind="quiet" size={48} onClick={notNow}>Not now</Button>
          </>}
        </div>
      </div>
    </div>
  );
}
